import { Divider, Typography } from 'antd'
import { useDebouncedLoading } from 'features/indicationTriage'
import {
  DownwardIndicator,
  FfsResultIcon,
} from 'features/indicationTriage/cards/ffsCalc/FfsResultIcon'
import { useSavedReports } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import { FfsReportPanel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsReportPanel'
import {
  FfsAnalysisResultsSection,
  useAnalysisResult,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsResultPanel'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useFfsScenarios } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/useFfsScenarios'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { FfsAnalysisResult } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import { useFfsMawpProps } from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import { FfsUserInputSection } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsLevelOneUserInputSection'
import { FfsCalcCardContentProps } from 'features/indicationTriage/cards/FfsCalcCard'
import React, { FC, useContext, useMemo, useState } from 'react'

import {
  FfsPartFourCalculatedValues,
  FfsPartFourCalculatedValuesSection,
  usePartFourCalculatedValues,
} from './FfsCalculatedValuesSection'
import { FfsPartFourDataValueSection } from './FfsDataValuesSection'
import {
  FfsFinalAcceptabilityCriteriaSection,
  FinalAcceptabilityCriteriaCheckResult,
  useFinalAcceptabilityCriteriaCheck,
} from './FfsFinalAcceptabilityCriteriaSection'
import {
  getEffectiveThickness,
  useThicknessCriteriaCheck,
} from './FfsThicknessCriteriaSection'

const { Title } = Typography

const SPIN_DEBOUNCE_DELAY = 300

const RawFfsPartFourCalc: FC<FfsCalcCardContentProps> = ({ ffsLevel }) => {
  const {
    fca,
    setFca,
    fcaMl,
    setFcaMl,
    ffsDataValues,
    yieldStrength,
    setYieldStrengthFromScenario,
    weldStrengthReduction,
    setWeldStrengthReduction,
    setQualityFactorFromScenario,
    setPressureCoefficientFromScenario,
    setTslFromScenario,
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness,
    calcType,
    setCalcType,
    designPressure,
  } = useContext(FfsSharedValuesContext)

  const partFourDataValues = ffsDataValues

  const {
    loading: dataValuesLoading = false,
    tmm = null,
    trd = null,
    t_ml = null,
    tc = null,
    d_ml = null,
    outerDiameter = null,
    nominal = null,
  } = partFourDataValues || {}

  // Calculate Part 4 Final Acceptability Criteria
  const partFourCalculatedValues: FfsPartFourCalculatedValues =
    usePartFourCalculatedValues({
      tmm,
      tc,
      fcaMl,
      t_ml,
      d_ml,
    })

  const calculatedValuesForPlot = useMemo(
    () => ({
      tnom: nominal,
      trd,
      tc,
      tmm: tmm,
      fca: fca,
      fcaMl: fcaMl,
    }),
    [nominal, trd, tc, tmm, fca, fcaMl]
  )

  const { ts_am, tc_am, cdfDataLoading } = partFourCalculatedValues

  const effectiveThicknessResult = useMemo(
    () =>
      getEffectiveThickness({
        t_l: selectedMawpCalcThickness === 't_mm' ? tmm : ts_am,
        t_c: selectedMawpCalcThickness === 't_mm' ? tmm : tc_am,
        tmm,
        fcaMl,
      }),
    [selectedMawpCalcThickness, tmm, ts_am, tc_am, fcaMl]
  )

  const mawpProps = useFfsMawpProps({
    t_c: effectiveThicknessResult.tcEffective,
    t_l: effectiveThicknessResult.tsEffective,
    outerDiameter,
    rsfa: partFourCalculatedValues.rsfa,
    rsf: partFourCalculatedValues.rt,
    designPressure,
    tNom: nominal,
    calcType,
    ffsLevel,
  })

  const thicknessCriteriaCheckResult = useThicknessCriteriaCheck({
    tmm,
    fcaMl,
    effectiveMeasuredThickness:
      effectiveThicknessResult.effectiveMeasuredThickness,
    outerDiameter,
    designPressure,
    nominalThickness: nominal,
    yieldStrength,
    qualityFactor: mawpProps.qualityFactor,
    pressureCoefficient: mawpProps.pressureCoefficient,
    weldStrengthReduction,
    tsl: mawpProps.tsl,
    tNom: nominal,
    calcType,
  })

  const finalAcceptabilityCriteriaCheckResult: FinalAcceptabilityCriteriaCheckResult =
    useFinalAcceptabilityCriteriaCheck({
      failingThicknessCriteria:
        thicknessCriteriaCheckResult.failingThicknessCriteria,
      failingMawpCriteria: mawpProps.failingMawpCriteria,
    })

  const analysisResult: FfsAnalysisResult = useAnalysisResult({
    mawpr: mawpProps.mawpr,
    mawpDesign: mawpProps.mawpDesign,
    resultProps: {
      finalAcceptabilityCriteriaCheckProps:
        finalAcceptabilityCriteriaCheckResult,
    },
  })

  const loading = useDebouncedLoading(SPIN_DEBOUNCE_DELAY, [
    cdfDataLoading,
    dataValuesLoading,
  ])

  // Reports and scenarios management
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(
    null
  )
  const {
    reports,
    isLoading: reportsLoading,
    forceReload: reloadReports,
    setDefaultReport,
  } = useSavedReports()

  useFfsScenarios({
    selectedScenarioId,
    setSelectedScenarioId,
    rsf_a: partFourCalculatedValues.rsfa ?? null,
    setRsf_a: partFourCalculatedValues.setRsfa,
    e: mawpProps.qualityFactor,
    setE: setQualityFactorFromScenario,
    y: mawpProps.pressureCoefficient,
    setY: setPressureCoefficientFromScenario,
    w: weldStrengthReduction,
    setW: setWeldStrengthReduction,
    s: yieldStrength,
    setS: setYieldStrengthFromScenario,
    tsl: mawpProps.tsl,
    setTsl: setTslFromScenario,
    fca,
    setFca,
    fca_ml: fcaMl,
    setFca_ml: setFcaMl,
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness: setSelectedMawpCalcThickness,
    scenarios: reports,
    calcType,
    setCalcType,
    ffsLevel,
    ffsPart: FfsPart.PART_4,
  })

  return (
    <>
      <Title level={4}>User Parameters</Title>
      <FfsUserInputSection />
      <Divider />
      <Title level={4}>Data Values</Title>
      {partFourDataValues && (
        <FfsPartFourDataValueSection {...partFourDataValues} />
      )}
      <Divider />
      <Title level={4}>Calculated Values</Title>
      <FfsPartFourCalculatedValuesSection
        loading={loading}
        {...partFourCalculatedValues}
        {...calculatedValuesForPlot}
      />
      <Divider />
      <Title level={4}>Acceptability Criteria</Title>
      <FfsFinalAcceptabilityCriteriaSection
        loading={loading}
        mawpProps={mawpProps}
        thicknessCriteriaCheckProps={{
          ...thicknessCriteriaCheckResult,
        }}
        ffsLevel={ffsLevel}
      />
      <Divider>
        <FfsResultIcon
          passed={finalAcceptabilityCriteriaCheckResult.passesFac}
          tooltipFailureText='Failed acceptability criteria. Derating required'
          trailingFail={DownwardIndicator}
        />
      </Divider>
      <Title level={4}>Results</Title>
      <FfsAnalysisResultsSection analysisResult={analysisResult} />
      <Divider />
      <Title level={4}>Reports</Title>
      <FfsReportPanel
        calculatedValues={partFourCalculatedValues}
        thicknessCriteria={thicknessCriteriaCheckResult}
        mawpProps={mawpProps}
        ffsPart={FfsPart.PART_4}
        ffsLevel={ffsLevel}
        reports={reports}
        reportsLoading={reportsLoading}
        reloadReports={reloadReports}
        setDefaultReport={setDefaultReport}
        setSelectedScenarioId={setSelectedScenarioId}
        analysisResult={analysisResult}
      />
    </>
  )
}

export const FfsPartFourCalc = React.memo(RawFfsPartFourCalc)
